import { Stack, Typography } from "@mui/material";
import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip, LoadingOverlay, TableColumn, TableComponent } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	useDeleteFormatPresetMutation,
	useGetFormatPresetsQuery,
	useUpdateFormatPresetMutation,
} from "@/pages/Private/redux/media/media.api";
import { FormatPreset, FormatPresetScope } from "@/pages/Private/redux/media/media.schema";

import { FormatPresetModal } from "./FormatPresetModal";

export const FormatPresetsTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`mediaPresets.${key}`);

	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(25);
	const [editing, setEditing] = useState<FormatPreset | null>(null);
	const [isCreating, setIsCreating] = useState(false);

	const { data: presets, isLoading } = useGetFormatPresetsQuery();
	const [updatePreset] = useUpdateFormatPresetMutation();
	const [deletePreset, { isLoading: isDeleting }] = useDeleteFormatPresetMutation();

	const columns: TableColumn<FormatPreset>[] = useMemo(
		() => [
			{
				label: ts("columns.label"),
				minWidth: 160,
				format: (row) => (
					<Stack direction="column">
						<Typography className="text-ink" variant="subtitle2">
							{row.label}
						</Typography>
						<Typography className="text-subtle" fontWeight="400" variant="body2">
							{row.key}
						</Typography>
					</Stack>
				),
			},
			{
				label: ts("columns.dimensions"),
				minWidth: 140,
				format: (row) => `${row.width} × ${row.height}`,
			},
			{
				label: ts("columns.aspectRatio"),
				minWidth: 100,
				format: (row) => row.aspectRatio,
			},
			{
				label: ts("columns.mediaKind"),
				minWidth: 120,
				format: (row) => ts(`mediaKind.${row.mediaKind.toLowerCase()}`),
			},
			{
				label: ts("columns.scope"),
				minWidth: 120,
				format: (row) => (
					<Chip
						color={row.scope === FormatPresetScope.GLOBAL ? "secondary" : "success"}
						label={ts(`scope.${row.scope.toLowerCase()}`)}
					/>
				),
			},
			{
				label: ts("columns.status"),
				minWidth: 110,
				format: (row) => (
					<Chip
						color={row.enabled ? "success" : "secondary"}
						label={row.enabled ? ts("status.enabled") : ts("status.disabled")}
					/>
				),
			},
			{
				label: "",
				minWidth: 190,
				align: "right",
				format: (row) => (
					<Stack direction="row" justifyContent="flex-end" spacing={1}>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							title={ts("actions.edit")}
							type="button"
							onClick={() => setEditing(row)}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							title={row.enabled ? ts("actions.disable") : ts("actions.enable")}
							type="button"
							onClick={() => updatePreset({ id: row.id, enabled: !row.enabled })}
						/>
						<Button
							color={ButtonColor.DANGER}
							size={ButtonSize.XS}
							title={ts("actions.delete")}
							type="button"
							onClick={() => deletePreset(row.id)}
						/>
					</Stack>
				),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	return (
		<section className="rounded-card border border-border-soft bg-white p-5">
			<div className="mb-4 flex flex-wrap items-start justify-between gap-3">
				<div>
					<h3 className="text-ink">{ts("formats.title")}</h3>
					<p className="mt-1 text-sm text-subtle">{ts("formats.description")}</p>
				</div>
				<Button
					className="!w-auto"
					color={ButtonColor.BRAND}
					size={ButtonSize.S}
					title={ts("actions.addPreset")}
					type="button"
					onClick={() => setIsCreating(true)}
				/>
			</div>

			{isLoading || isDeleting ? <LoadingOverlay /> : null}

			<TableComponent<FormatPreset>
				columns={columns}
				data={presets ?? []}
				handleChangeLimit={(event) => {
					setLimit(+event.target.value);
					setPage(0);
				}}
				handleChangePage={(_, nextPage) => setPage(nextPage)}
				isLoading={isLoading}
				itemCount={presets?.length ?? 0}
				limit={limit}
				noDataText={ts("formats.empty")}
				page={page}
			/>

			<FormatPresetModal
				handleClose={() => {
					setIsCreating(false);
					setEditing(null);
				}}
				isOpened={isCreating || Boolean(editing)}
				preset={editing}
			/>
		</section>
	);
};
